import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronRight, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { ScheduleListSheet } from "@/components/word-form/ScheduleListSheet"
import { getWordForm } from "@/lib/api"
import { useBidReadOnly } from "@/hooks/useViewOnly"
import { stripExtension } from "@/lib/workbook"
import { SCHEDULE_PARAM } from "@/lib/word-form"
import { useWordFormStore } from "@/stores/word-form-store"
import type { FormField, FormHeading } from "@/types/word-form"

export interface DocumentSchedulesLinkProps {
  bidId: string
  documentId: string
  documentName: string
  readOnly?: boolean
}

const CREATE_FAILED = "Couldn't set up this form. Try again."

function formPathFor(bidId: string, artifactId: string): string {
  return `/app/bids/${bidId}/responses/${artifactId}/form`
}

/**
 * "6 schedules found" under a Word tender file, with "Fill this form" until
 * the file has a form. The count comes from the bid's one schedules call
 * (`loadBidSchedules`, made by the list); the schedule list itself loads only
 * when opened. Renders nothing for a file with no schedules.
 */
export function DocumentSchedulesLink({
  bidId,
  documentId,
  documentName,
  readOnly: readOnlyProp = false,
}: DocumentSchedulesLinkProps) {
  const navigate = useNavigate()
  // The page passes its own answer; the bid's access is checked here too, so
  // the link never offers a write the server would refuse.
  const access = useBidReadOnly(bidId)
  const readOnly = readOnlyProp || access.isReadOnly
  const summary = useWordFormStore((s) =>
    s.bidSchedules?.bidId === bidId ? s.bidSchedules.byDocument[documentId] : undefined
  )
  const entry = useWordFormStore((s) => s.documentSchedules[documentId])
  const loadDocumentSchedules = useWordFormStore((s) => s.loadDocumentSchedules)
  const correctSchedules = useWordFormStore((s) => s.correctSchedules)
  const createForm = useWordFormStore((s) => s.createForm)
  const isCorrecting = useWordFormStore((s) => s.isCorrecting)
  const correctionError = useWordFormStore((s) => s.correctionError)
  const clearCorrectionError = useWordFormStore((s) => s.clearCorrectionError)
  const isCreating = useWordFormStore((s) => s.creatingFormFor === documentId)
  const [open, setOpen] = useState(false)
  /** Split and Add pick from the form's headings and fields, so they load with the sheet. */
  const [formDetail, setFormDetail] = useState<{ fields: FormField[]; headings: FormHeading[] }>({
    fields: [],
    headings: [],
  })

  const artifactId = entry?.wordFormArtifactId ?? summary?.wordFormArtifactId ?? null

  useEffect(() => {
    if (!open) return
    void loadDocumentSchedules(bidId, documentId, true)
  }, [open, bidId, documentId, loadDocumentSchedules])

  useEffect(() => {
    if (!open || !artifactId) return
    let cancelled = false
    getWordForm(artifactId)
      .then((info) => {
        if (!cancelled) setFormDetail({ fields: info.fields, headings: info.headings ?? [] })
      })
      .catch(() => {
        // Split and Add stay unavailable; the rest of the list still works.
      })
    return () => {
      cancelled = true
    }
  }, [open, artifactId])

  const count = summary?.scheduleCount ?? 0
  if (count === 0) return null

  const schedules = entry?.schedules ?? []
  const isLoadingList = entry?.status === "loading" && schedules.length === 0
  const formPath = artifactId ? formPathFor(bidId, artifactId) : null
  const canCreate = !artifactId && !readOnly
  const canCorrect = Boolean(artifactId) && !readOnly

  const handleFill = async () => {
    const created = await createForm(bidId, documentId, stripExtension(documentName))
    if (!created) {
      toast.error(useWordFormStore.getState().error ?? CREATE_FAILED)
      return
    }
    void navigate(formPathFor(bidId, created.artifactId))
  }

  const fillLabel = isCreating ? "Setting up…" : "Fill this form"

  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-0.5 text-meta text-[hsl(var(--link))] hover:underline"
        >
          {count} {count === 1 ? "schedule" : "schedules"} found
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        {canCreate && (
          <>
            <span className="text-meta" aria-hidden="true">
              ·
            </span>
            <button
              type="button"
              onClick={() => void handleFill()}
              disabled={isCreating}
              className="text-meta text-[hsl(var(--link))] hover:underline disabled:no-underline"
            >
              {fillLabel}
            </button>
          </>
        )}
      </span>

      <ScheduleListSheet
        open={open}
        onOpenChange={setOpen}
        filename={documentName}
        schedules={schedules}
        scheduleCount={isLoadingList ? count : schedules.length}
        isLoading={isLoadingList}
        fields={formDetail.fields}
        headings={formDetail.headings}
        isBusy={isCorrecting}
        error={correctionError}
        onClearError={clearCorrectionError}
        {...(formPath
          ? {
              action: (
                <Button size="sm" onClick={() => void navigate(formPath)}>
                  Open form
                </Button>
              ),
            }
          : canCreate
            ? {
                action: (
                  <Button size="sm" onClick={() => void handleFill()} disabled={isCreating}>
                    {isCreating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    {fillLabel}
                  </Button>
                ),
                note: "Fill this form to open the schedules and correct the list.",
              }
            : { note: "No one has started filling this form yet." })}
        {...(canCorrect && artifactId
          ? { onCorrect: (correction) => correctSchedules(artifactId, correction) }
          : {})}
        {...(formPath
          ? {
              onOpenSchedule: (scheduleId: string) =>
                void navigate(`${formPath}?${SCHEDULE_PARAM}=${encodeURIComponent(scheduleId)}`),
            }
          : {})}
      />
    </>
  )
}
